import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";

/**
 * "SAINT vs lower-cost copies" — a reusable, quotable content block.
 *
 * Placed high on SAINT cost / overview pages so search engines and AI
 * answer engines can quote the real price ($25,000) and the reason a
 * ~$9,900 "fMRI-guided accelerated TMS" program is a different treatment,
 * not a cheaper version of SAINT®.
 *
 * All copy is real HTML text (no images) so it can be quoted.
 * Desktop: a 2-column comparison table (SAINT® vs Lower-cost accelerated TMS).
 * Mobile: the same dimensions as stacked bullets.
 */

interface CompareRow {
  label: string;
  saint: string;
  copy: string;
}

const ROWS: CompareRow[] = [
  {
    label: "FDA-cleared targeting",
    saint:
      "The only FDA-cleared system that uses an fMRI brain scan to personalize where TMS is aimed (FDA 510(k) K220177, September 2022).",
    copy: "Use their own targeting software, which is not FDA-cleared for this purpose.",
  },
  {
    label: "Off-label",
    saint: "Cleared by the FDA for this use.",
    copy: "Their own providers describe these programs as an off-label use of a TMS device.",
  },
  {
    label: "Evidence",
    saint:
      "78.6% remission in the 2022 randomized controlled trial (Cole et al., Am J Psychiatry 2022;179:132–141) — results come only from SAINT.",
    copy: "Providers state their protocol has not been tested in the SAINT clinical trials, so those results do not carry over.",
  },
  {
    label: "Verification",
    saint:
      "Only clinics listed on Magnus Medical's provider locator deliver SAINT®. Neuro Wellness TMS Centers of America is the verified provider in Florida.",
    copy: "Not listed on the Magnus Medical provider locator.",
  },
  {
    label: "Insurance",
    saint:
      "Has a Medicare payment rate ($19,703 hospital outpatient, set by CMS in July 2025); commercial plans may approve it through prior authorization.",
    copy: "Typically self-pay only.",
  },
  {
    label: "Real price",
    saint:
      "Starts at $25,000 for the full five-day course, including the fMRI scan, all 50 sessions, the clinical team, and follow-up — lower than the $30,000–36,000 figure often quoted online. We verify your benefits for free.",
    copy: "Often around $9,900 — but for a different treatment, not a discount on SAINT®.",
  },
];

export const SaintVersusCopiesBlock = () => (
  <div className="not-prose my-8 rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6">
    <h2 className="font-headings text-xl font-bold leading-snug text-primary sm:text-2xl">
      Is a $9,900 “fMRI-guided accelerated TMS” program the same as SAINT®?
    </h2>

    <p className="mt-3 text-[1.0625rem] leading-relaxed text-foreground">
      No. Programs priced around $9,900 copy SAINT's schedule (5 days, 10
      sessions a day, about 90,000 pulses). They do not copy the parts that make
      it SAINT®, so the two prices are for two different treatments.
    </p>

    {/* Desktop: 2-column comparison table */}
    <div className="mt-5 hidden overflow-hidden rounded-xl border border-border md:block">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th
              scope="col"
              className="w-40 px-4 py-3 text-left font-headings font-bold text-primary"
            >
              &nbsp;
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left font-headings font-bold text-accent"
            >
              SAINT®
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left font-headings font-bold text-muted-foreground"
            >
              Lower-cost accelerated TMS
            </th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr
              key={r.label}
              className="border-b border-border/60 align-top last:border-0"
            >
              <th
                scope="row"
                className="px-4 py-3 text-left font-headings text-sm font-bold text-primary"
              >
                {r.label}
              </th>
              <td className="px-4 py-3 leading-relaxed text-foreground">
                {r.saint}
              </td>
              <td className="px-4 py-3 leading-relaxed text-muted-foreground">
                {r.copy}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Mobile: stacked bullets */}
    <ul className="mt-5 space-y-4 md:hidden">
      {ROWS.map((r) => (
        <li
          key={r.label}
          className="rounded-xl border border-border/70 bg-muted/20 p-4"
        >
          <p className="font-headings text-sm font-bold text-primary">
            {r.label}
          </p>
          <p className="mt-1.5 text-[0.95rem] leading-relaxed text-foreground">
            <span className="font-semibold text-accent">SAINT®:</span> {r.saint}
          </p>
          <p className="mt-1 text-[0.95rem] leading-relaxed text-muted-foreground">
            <span className="font-semibold">Lower-cost accelerated TMS:</span>{" "}
            {r.copy}
          </p>
        </li>
      ))}
    </ul>

    <p className="mt-5 text-[1.0625rem] leading-relaxed text-foreground">
      <strong className="font-semibold text-primary">Bottom line:</strong>{" "}
      comparing $9,900 to SAINT® compares an untested copy of the schedule to
      the FDA-cleared treatment. It is not a discount on the same thing.
    </p>

    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
      <Link
        to="/learn/who-qualifies-for-saint-tms"
        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-sm font-bold text-accent-foreground shadow-glow transition hover:opacity-90"
      >
        See if I may be a candidate
        <ArrowRight className="h-4 w-4" />
      </Link>
      <Link
        to="/learn/saint-vs-accelerated-tms"
        className="inline-flex items-center gap-1.5 font-semibold text-accent underline underline-offset-2 hover:opacity-80"
      >
        Full side-by-side
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>

    <p className="mt-4 text-xs text-muted-foreground">
      Individual results vary. No outcome is guaranteed. Coverage varies by
      plan.
    </p>
  </div>
);

export default SaintVersusCopiesBlock;
